/**
 * Piping Testing (ASME B31.3 / B31.1 Pressure Testing) Discipline Configuration
 *
 * Configuration for the Testing Tracker sub-view of the piping dashboard.
 * Modeled on pipingNde.js. Tracks pressure testing (hydrostatic, pneumatic,
 * leak testing, etc.) as one row per ISO sheet x test package combination.
 */

export const FIELD_MAP = {
  isoSheetNumber: 'iso_sheet_number',
  testPackageNumber: 'test_package_number',
  lineSystemId: 'line_system_id',
  fluidCode: 'fluid_code',
  systemDescription: 'system_description',
  designCode: 'design_code',
  designPressPsi: 'design_press_psi',
  designTempF: 'design_temp_f',
  testPressPsi: 'test_press_psi',
  testType: 'test_type',
  testMedium: 'test_medium',
  holdTimeMin: 'hold_time_min',
  status: 'status',
  testDate: 'test_date',
  inspector: 'inspector',
  signOff: 'sign_off',
  notes: 'notes',
};

export const EDITABLE_COLUMNS = [
  'isoSheetNumber', 'testPackageNumber',
  'lineSystemId', 'fluidCode', 'systemDescription', 'designCode',
  'designPressPsi', 'designTempF', 'testPressPsi', 'testType', 'testMedium',
  'holdTimeMin', 'status', 'testDate', 'inspector', 'signOff', 'notes',
];

export const STATUS_OPTIONS = ['', 'Pending', 'In Progress', 'Pass / Complete', 'Failed'];
export const TEST_TYPE_OPTIONS = ['', 'Hydrostatic', 'Pneumatic', 'Service Leak', 'Sensitive Leak', 'Alternative Leak'];
export const TEST_MEDIUM_OPTIONS = ['', 'Water (Filtered)', 'Dry Nitrogen', 'Clean Compressed Air', 'Service Fluid', 'Helium/Air Mixture'];

export const transformMatrixData = (matrixData) => {
  return matrixData.map((row, index) => ({
    id: row.id || index,
    uuid: row.uuid || '',
    modifiedBy: row.modified_by || '',
    isoSheetNumber: row.iso_sheet_number || '',
    testPackageNumber: row.test_package_number || '',
    lineSystemId: row.line_system_id || '',
    fluidCode: row.fluid_code || '',
    systemDescription: row.system_description || '',
    designCode: row.design_code || '',
    designPressPsi: row.design_press_psi != null ? row.design_press_psi : '',
    designTempF: row.design_temp_f != null ? row.design_temp_f : '',
    testPressPsi: row.test_press_psi || '',
    testType: row.test_type || '',
    testMedium: row.test_medium || '',
    holdTimeMin: row.hold_time_min || '',
    status: row.status || 'Pending',
    testDate: row.test_date || '',
    inspector: row.inspector || '',
    signOff: row.sign_off || '',
    notes: row.notes || '',
  }));
};

export const buildChangePayload = (rowId, columnId, oldValue, newValue) => {
  const backendField = FIELD_MAP[columnId];
  if (!backendField) return null;

  return {
    rowId,
    field: backendField,
    value: newValue,
  };
};

export const pipingTestingConfig = {
  id: 'piping-testing',
  name: 'Testing Tracker',
  menuId: 'piping-testing',

  api: {
    getData: '/pipe-testing/project/{projectId}',
    saveChanges: '/pipe-testing/save-changes',
    export: '/pipe-testing/export/{projectId}',
    template: '/pipe-testing/template/{projectId}',
    analytics: '/pipe-testing/analytics/{projectId}',
    availableFluidCodes: '/pipe-testing/available-fluid-codes/{projectId}',
    importFromFluidCodes: '/pipe-testing/import-from-fluid-codes',
    availableTestPackages: '/pipe-testing/available-test-packages/{projectId}',
    importFromTestPackages: '/pipe-testing/import-from-test-packages',
    importExcel: '/pipe-testing/import-excel',
    addRecord: '/pipe-testing/add-record',
    deleteRecords: '/pipe-testing/records',
    duplicateRecords: '/pipe-testing/duplicate-records',
    auditLog: '/pipe-testing-audit-log/project/{projectId}',
  },

  permissions: {
    edit: 'pt_edit',
    editAll: 'pt_edit_all',
    analytics: 'pt_analytics',
    import: 'pt_import',
    export: 'pt_export',
    columnConfig: 'pt_columnconfig',
    delete: 'pt_delete',
  },

  columns: [
    {
      id: 'isoSheetNumber',
      header: 'ISO Sheet Number',
      accessor: 'isoSheetNumber',
      dbField: 'iso_sheet_number',
      width: 150,
      editable: true,
      filterable: true,
      type: 'text',
      headerColor: '#E8F5E9',
      headerGroup: 'System',
    },
    {
      id: 'testPackageNumber',
      header: 'Test Package Number',
      accessor: 'testPackageNumber',
      dbField: 'test_package_number',
      width: 140,
      editable: true,
      filterable: true,
      type: 'text',
      headerColor: '#E8F5E9',
      headerGroup: 'System',
    },
    {
      id: 'lineSystemId',
      header: 'Line / System ID',
      accessor: 'lineSystemId',
      dbField: 'line_system_id',
      width: 160,
      editable: true,
      filterable: true,
      type: 'text',
      headerColor: '#E8F5E9',
      headerGroup: 'System',
    },
    {
      id: 'fluidCode',
      header: 'Fluid Code',
      accessor: 'fluidCode',
      dbField: 'fluid_code',
      width: 110,
      editable: true,
      filterable: true,
      type: 'text',
      headerColor: '#E8F5E9',
      headerGroup: 'System',
    },
    {
      id: 'systemDescription',
      header: 'System Description',
      accessor: 'systemDescription',
      dbField: 'system_description',
      width: 220,
      editable: true,
      filterable: true,
      type: 'text',
      headerColor: '#E8F5E9',
      headerGroup: 'System',
    },
    {
      id: 'designCode',
      header: 'Design Code',
      accessor: 'designCode',
      dbField: 'design_code',
      width: 120,
      editable: true,
      filterable: true,
      type: 'text',
      headerColor: '#E3F2FD',
      headerGroup: 'Design',
    },
    {
      id: 'designPressPsi',
      header: 'Design Press. (PSI)',
      accessor: 'designPressPsi',
      dbField: 'design_press_psi',
      width: 140,
      editable: true,
      filterable: true,
      type: 'number',
      headerColor: '#E3F2FD',
      headerGroup: 'Design',
    },
    {
      id: 'designTempF',
      header: 'Design Temp. (°F)',
      accessor: 'designTempF',
      dbField: 'design_temp_f',
      width: 140,
      editable: true,
      filterable: true,
      type: 'number',
      headerColor: '#E3F2FD',
      headerGroup: 'Design',
    },
    {
      id: 'testPressPsi',
      header: 'Test Press. (PSI)',
      accessor: 'testPressPsi',
      dbField: 'test_press_psi',
      width: 140,
      editable: true,
      filterable: true,
      type: 'text',
      headerColor: '#FFF3E0',
      headerGroup: 'Test',
    },
    {
      id: 'testType',
      header: 'Type of Testing',
      accessor: 'testType',
      dbField: 'test_type',
      width: 150,
      editable: true,
      filterable: true,
      type: 'select',
      options: TEST_TYPE_OPTIONS,
      headerColor: '#FFF3E0',
      headerGroup: 'Test',
    },
    {
      id: 'testMedium',
      header: 'Test Medium',
      accessor: 'testMedium',
      dbField: 'test_medium',
      width: 170,
      editable: true,
      filterable: true,
      type: 'select',
      options: TEST_MEDIUM_OPTIONS,
      headerColor: '#FFF3E0',
      headerGroup: 'Test',
    },
    {
      id: 'holdTimeMin',
      header: 'Hold Time (Min)',
      accessor: 'holdTimeMin',
      dbField: 'hold_time_min',
      width: 130,
      editable: true,
      filterable: true,
      type: 'text',
      headerColor: '#FFF3E0',
      headerGroup: 'Test',
    },
    {
      id: 'status',
      header: 'Completed / Status',
      accessor: 'status',
      dbField: 'status',
      width: 160,
      editable: true,
      filterable: true,
      type: 'select',
      options: STATUS_OPTIONS,
      headerColor: '#FCE4EC',
      headerGroup: 'Result',
    },
    {
      id: 'testDate',
      header: 'Test Date',
      accessor: 'testDate',
      dbField: 'test_date',
      width: 130,
      editable: true,
      filterable: true,
      type: 'date',
      headerColor: '#FCE4EC',
      headerGroup: 'Result',
    },
    {
      id: 'inspector',
      header: 'Welder #',
      accessor: 'inspector',
      dbField: 'inspector',
      width: 140,
      editable: true,
      filterable: true,
      type: 'select',
      options: [],
      dynamicOptionsUrl: '/welder-log/all',
      dynamicOptionsKey: 'welder_number',
      headerColor: '#FCE4EC',
      headerGroup: 'Result',
    },
    {
      id: 'signOff',
      header: 'Sign-off',
      accessor: 'signOff',
      dbField: 'sign_off',
      width: 140,
      editable: true,
      filterable: true,
      type: 'text',
      headerColor: '#FCE4EC',
      headerGroup: 'Result',
    },
    {
      id: 'notes',
      header: 'Notes / Comments',
      accessor: 'notes',
      dbField: 'notes',
      width: 220,
      editable: true,
      filterable: true,
      type: 'text',
    },
    {
      id: 'modifiedBy',
      header: 'Modified By',
      accessor: 'modifiedBy',
      dbField: 'modified_by',
      width: 150,
      editable: false,
      filterable: true,
      type: 'text',
    },
  ],

  hiddenColumns: [],

  features: {
    dragFill: true,
    progressTrends: false,
    rulesOfCredit: false,
    shopFieldRatio: false,
    matBore: false,
    fabricationShops: false,
    priorities: false,
    columnMappings: false,
    udfColumns: false,
    presence: true, // Real-time presence tracking (shows who's viewing)
  },

  transformData: transformMatrixData,
  buildChangePayload,
  FIELD_MAP,
  EDITABLE_COLUMNS,
};
